import {
  AirbaseLocation,
  CpdLocation,
  InstallationLocation,
  Profile,
  SpacebaseLocation,
  WorkExperience,
  UserCompetencies,
  ProfileWorkType,
  School,
  CipCodeForGoal,
  AsvabScores
} from '@cpd-app/generated';

import {
  Aspirations,
  Location,
  UserInterest,
  UserWorkValue
} from '../../models';
import { CivilianWorkExperienceModel } from '../civilian-work-experience';
import { Education } from '../education/education.model';
import { MilitaryBranch, Moc, Rank } from '../military';

export class UserProfile {
  // THE FOLLOWING PROPERTIES MAP TO Profile CLASS FROM CPD SUBGRAPH GRAPHQL
  userId = 0;
  profileType?: string = null;
  isSkillsSurveyComplete?: boolean = false;
  isInterestsSurveyComplete?: boolean = false;
  isWorkValuesSurveyComplete?: boolean = false;
  isLifestyleSurveyComplete?: boolean = false;
  isJstUploaded?: boolean = false;
  hasCivilianWorkExperience?: boolean = false;
  hasReturnUrl?: boolean = false;
  privacyPolicyAccepted?: boolean = false;
  returnUrlLastSavedDate?: Date;
  serveTypeId?: number = null;
  reportDownloaded?: boolean = false;
  reportShared?: boolean = false;
  militaryBranch?: MilitaryBranch = null;
  profileWorkType?: ProfileWorkType = null;
  userRanks?: Rank[] = null;
  userMocs?: Moc[] = null;
  userWorkExperiences?: CivilianWorkExperienceModel[] = null;
  userEducations?: Education[] = null;
  userPreferredLocations: Location[] & CpdLocation[] = null;
  userCurrentLocations?: Location[] & CpdLocation[] = null;
  userInterests?: UserInterest[] = null;
  userWorkValues?: UserWorkValue[] = null;
  userAspirations?: Aspirations = null;
  userAspirationsPreference?: string[] = null;
  userCompetencies?: UserCompetencies;
  experienceAnalysis?: string;
  interestsAnalysis?: string;
  workValueAnalysis?: string;
  userHasPreGoal?: boolean = false;
  userPreGoalOccupation?: string;
  /**
   * The career pre-goal's O*NET code, beside the title above.
   *
   * The skills survey needs the code, not just the title: its work list is a
   * list of removable rows keyed on the O*NET code, so with only a title the
   * survey could show the saved answer but not let the member take it back
   * out. The code was always stored on api.userpregoals.onetcode; it was just
   * missing from the read contract.
   */
  userPreGoalOnetCode?: string;
  userAirbaseLocation?: AirbaseLocation = null;
  userSpacebaseLocation?: SpacebaseLocation = null;
  // DATA-1: the all-branch installation answer, which supersedes the two above
  // once INSTALLATION_CAPTURE_ENABLED is on.
  userInstallation?: InstallationLocation = null;
  userProgramGoal?: CipCodeForGoal = null;
  /**
   * How far the member said they want to go, as an api.educationlevel id.
   *
   * The third fact of the schooling pre-goal, next to userProgramGoal (in
   * what) and userSchoolGoal (where). Kept as its own property rather than on
   * CipCodeForGoal because it is a separate answer: "a Bachelor's, I do not
   * know in what" is exactly the case that was being dropped.
   */
  userProgramGoalEdLevelId?: number = null;
  userSchoolGoal?: School = null;
  userWorkTermination: number = null; // in months, maps to Profile.
  userMilitaryTermination: number = null; // in months, maps to Profile.
  // PWS 4.1.12. Null when the member has entered none of the four, which is
  // different from having entered a zero.
  asvabScores?: AsvabScores = null;
  /**
   * ASVAB.5. Every score the member has, as scale/score pairs.
   *
   * asvabScores above carries only the four line scores that have columns, and
   * those are Army and Marine Corps composites. An Air Force or Space Force
   * member's MAGE scores, a Navy or Coast Guard member's subtests, and anyone's
   * printed Career Exploration Scores exist ONLY here. Null rather than [] when
   * nothing has been entered, so "not asked" stays distinguishable.
   */
  asvabScoreSet?: { scale: string; score: number }[] = null;
  userRole: string;
  userEmail: string;
  status: string;
  firstName: string;
  lastName: string;

  get mpcCodeList(): string {
    return this.userRanks
      ? this.userRanks
          .map((x) => x.mpcCode)
          .filter((value, index, self) => self.indexOf(value) === index)
          .sort()
          .join()
      : null;
  }

  constructor(profile: Profile) {
    /*
     * No session means no profile, and that is an ordinary state rather than
     * a fault: `getSession` answers `{ isAuthenticated: false, profile: null }`
     * to anybody who has not signed in, and its caller reads every field off
     * the result with `?.` before handing the null straight to this line.
     * Dereferencing it threw a TypeError out of the session response, which
     * surfaced to the member as "Cannot read properties of null (reading
     * 'userId')" on a page they had done nothing wrong on.
     *
     * Returning early leaves the field initialisers standing, so an absent
     * profile is `userId = 0`. That is the sentinel the app already uses for
     * one: `UserProfileDataService.parseUserProfileData` tests `userId === 0`.
     */
    if (!profile) {
      return;
    }
    this.userId = profile.userId;
    this.profileType = profile.profileType ?? null;
    this.isSkillsSurveyComplete = profile.isSkillsSurveyComplete ?? false;
    this.isInterestsSurveyComplete = profile.isInterestsSurveyComplete ?? false;
    this.isWorkValuesSurveyComplete =
      profile.isWorkValuesSurveyComplete ?? false;
    this.isLifestyleSurveyComplete = profile.isLifestyleSurveyComplete ?? false;
    this.isJstUploaded = profile.isJstUploaded ?? false;
    this.hasCivilianWorkExperience = profile.hasCivilianWorkExperience ?? false;
    this.hasReturnUrl = profile.hasReturnUrl ?? false;
    this.privacyPolicyAccepted = profile.privacyPolicyAccepted ?? false;
    this.profileWorkType = profile.profileWorkType ?? null;
    this.returnUrlLastSavedDate = profile.returnUrlLastSavedDate ?? null;
    this.serveTypeId = profile.serveTypeId ?? null;
    this.reportDownloaded = profile.reportDownloaded ?? false;
    this.reportShared = profile.reportShared ?? false;

    this.militaryBranch = (profile.militaryBranch as MilitaryBranch) ?? null;

    this.userRanks = (profile.userRanks as Rank[]) ?? null;
    this.userMocs = (profile.userMocs as Moc[]) ?? null;

    this.userWorkExperiences =
      profile.userWorkExperiences?.map((item: WorkExperience) => ({
        id: item.id ?? 0,
        occupationTitle: item.occupationTitle ?? '',
        onetSocCode: item.onetSocCode ?? '',
        industryCode: item.industryCode ?? '',
        workExperienceCategoryId: +item.workExperienceCategoryId,
        numberOfMonthsOfWorkExperience: item.numberOfMonthsOfWorkExperience
      })) ?? null;

    this.userEducations = (profile.userEducations as Education[]) ?? null;
    this.userPreferredLocations =
      (profile.userPreferredLocations as Location[] & CpdLocation[]) ?? null;

    this.userCurrentLocations =
      (profile.userCurrentLocations as Location[] & CpdLocation[]) ?? null;

    this.userInterests = (profile.userInterests as UserInterest[]) ?? null;

    this.userWorkValues = (profile.userWorkValues as UserWorkValue[]) ?? null;

    this.userAspirations = profile.userAspirations
      ? {
          familyTypePreference: profile.userAspirations.familyTypePreference,
          housingPreference: profile.userAspirations.housingPreference,
          transportationPreference:
            profile.userAspirations.transportationPreference,
          childcarePreference: profile.userAspirations.childcarePreference,
          foodPreference: profile.userAspirations.foodPreference,
          spouseIsEmployed: profile.userAspirations.spouseIsEmployed,
          spouseIncome: profile.userAspirations.spouseIncome,
          numberOfChildren: profile.userAspirations.numberOfChildren
        }
      : null;

    this.userAspirationsPreference = profile.userAspirationsPreference;

    this.userCompetencies = profile.userCompetencies;

    this.experienceAnalysis = profile.experienceAnalysis ?? null;
    this.interestsAnalysis = profile.interestsAnalysis ?? null;
    this.workValueAnalysis = profile.workValueAnalysis ?? null;
    this.userHasPreGoal = profile.userHasPreGoal ?? false;
    this.userPreGoalOccupation = profile.userPreGoalOccupation ?? null;
    this.userPreGoalOnetCode = profile.userPreGoalOnetCode ?? null;
    this.userAirbaseLocation = profile.userAirbaseLocation ?? null;
    this.userSpacebaseLocation = profile.userSpacebaseLocation ?? null;
    this.userInstallation = profile.userInstallation ?? null;
    this.userEmail = profile.userEmail;
    this.userRole = profile.userRole;
    this.status = profile.status;
    this.firstName = profile.firstName;
    this.lastName = profile.lastName;
    this.userProgramGoal = profile.userProgramGoal ?? null;
    this.userProgramGoalEdLevelId = profile.userProgramGoalEdLevelId ?? null;
    this.userSchoolGoal = profile.userSchoolGoal ?? null;
    this.userWorkTermination = profile.userWorkTermination ?? null;
    this.userMilitaryTermination = profile.userMilitaryTermination ?? null;
    this.asvabScores = profile.asvabScores ?? null;
    this.asvabScoreSet = (profile as { asvabScoreSet?: { scale: string; score: number }[] })
      .asvabScoreSet ?? null;
  }
}
